import { useState, useEffect, useCallback } from 'react';
import useOnlineStatus from './useOnlineStatus';
import db from '../db';

/**
 * Simplified offline sync hook - stores offline members separately and syncs when online
 */
export const useOfflineSync = (onSyncComplete) => {
  const [offlineMembers, setOfflineMembers] = useState([]);
  const [syncStatus, setSyncStatus] = useState('idle');
  const [syncMessage, setSyncMessage] = useState('');
  const [lastFailedIds, setLastFailedIds] = useState([]);
  const isOnline = useOnlineStatus();
  const API_BASE_URL = 'https://aaapd.org.et/gallery_backend/api';

  // Load offline members from IndexedDB on mount
  useEffect(() => {
    const load = async () => {
      try {
        const rows = await db.offlineMembers?.toArray();
        setOfflineMembers(Array.isArray(rows) ? rows : []);
      } catch (error) {
        console.error('Failed to load offline members from IndexedDB:', error);
        setOfflineMembers([]);
      }
    };
    load();
  }, []);

  // Keep state in sync with IndexedDB changes (simple polling fallback)
  useEffect(() => {
    let timer;
    const poll = async () => {
      try {
        const rows = await db.offlineMembers?.toArray();
        setOfflineMembers(Array.isArray(rows) ? rows : []);
      } catch {}
      timer = setTimeout(poll, 3000);
    };
    poll();
    return () => clearTimeout(timer);
  }, []);

  /**
   * Add a member to offline storage (not database)
   */
  const addOfflineMember = useCallback(async (memberData) => {
    const offlineMember = {
      ...memberData,
      tempId: `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      isOfflinePending: true,
      savedOffline: true,
      createdAt: new Date().toISOString(),
    };

    try {
      const id = await db.offlineMembers?.add(offlineMember);
      const saved = { ...offlineMember, id };
      // refresh list
      const rows = await db.offlineMembers?.toArray();
      setOfflineMembers(Array.isArray(rows) ? rows : [saved]);
      return saved;
    } catch (error) {
      console.error('Failed to save offline member to IndexedDB:', error);
      setOfflineMembers((prev) => [...prev, offlineMember]);
      return offlineMember;
    }
  }, []);

  /**
   * Sync offline members with the database
   */
  const syncOfflineMembers = useCallback(async () => {
    try {
      setSyncStatus('syncing');
      setSyncMessage('Syncing offline members...');

      if (!isOnline) {
        setSyncStatus('idle');
        return;
      }

      const membersToSync = await db.offlineMembers?.toArray();
      if (!membersToSync || membersToSync.length === 0) {
        setSyncStatus('idle');
        return;
      }

      let successCount = 0;
      let failedCount = 0;
      const errorMessages = [];
      const failedIds = [];

      for (const member of membersToSync) {
        try {
          const formData = new FormData();
          // Append only the fields expected by addMember.php
          const fieldOrder = [
            'firstName','middleName','lastName','gender',
            'labourID','labourIDNumber',
            'dateOfBirth','age','phoneNumber','email',
            'location','subCity','houseNumber','woreda','specificLocation',
            'disabilityType','disabilityOther','disabilityDescription',
            'educationLevel','maritalStatus','numberOfFamilies',
            'employmentStatus','workPlace','officeName','workType','jobResponsibility',
            'supportNeeded','workNeeded','supportReceived',
            'idType','idNumber',
            'opdEmail'
          ];
          fieldOrder.forEach((key) => {
            const val = member[key];
            if (val !== undefined && val !== null && val !== '') {
              formData.append(key, val);
            }
          });

          // Append files if present (blobs)
          if (member.idFile instanceof Blob) formData.append('idFile', member.idFile);
          if (member.photoFile instanceof Blob) formData.append('photoFile', member.photoFile);

          const resp = await fetch(`${API_BASE_URL}/addMember.php`, { method: 'POST', body: formData });
          const data = await resp.json();
          if (!resp.ok || data.status !== 'success') throw new Error(data.message || 'Failed to add member');

          // On success, remove from offline queue
          await db.offlineMembers?.delete(member.id);
          successCount++;
        } catch (err) {
          console.error('Failed to sync member:', err);
          failedCount++;
          if (member?.id !== undefined) failedIds.push(member.id);
          try {
            // Try to extract meaningful message
            const msg = err?.message || 'Unknown error';
            errorMessages.push(msg);
          } catch {}
        }
      }

      // Refresh state
      const remaining = await db.offlineMembers?.toArray();
      setOfflineMembers(Array.isArray(remaining) ? remaining : []);

      setSyncStatus(failedCount === 0 ? 'success' : 'error');
      if (failedCount === 0) {
        setSyncMessage(`Synced ${successCount} members successfully`);
        setLastFailedIds([]);
      } else {
        // Prefer a specific backend reason if present (e.g., duplicate full name)
        const uniqueErrors = Array.from(new Set(errorMessages.filter(Boolean)));
        if (failedCount === 1 && uniqueErrors.length > 0) {
          setSyncMessage(`Sync Failed because ${uniqueErrors[0]}`);
        } else if (uniqueErrors.length > 0) {
          setSyncMessage(`Sync complete with ${failedCount} failures: ${uniqueErrors.slice(0,3).join(' | ')}`);
        } else {
          setSyncMessage(`Sync complete with ${failedCount} failures`);
        }
        setLastFailedIds(failedIds);
      }

      if (onSyncComplete) onSyncComplete(successCount, failedCount);
    } catch (error) {
      console.error('Sync error:', error);
      setSyncStatus('error');
      setSyncMessage('Error syncing members');
    }
  }, [isOnline, onSyncComplete]);

  // Auto-sync when back online if there are queued items
  useEffect(() => {
    if (!isOnline) return;
    let timer;
    const checkAndSync = async () => {
      const count = await db.offlineMembers?.count();
      if (count > 0) {
        timer = setTimeout(() => syncOfflineMembers(), 1000);
      }
    };
    checkAndSync();
    return () => clearTimeout(timer);
  }, [isOnline, syncOfflineMembers]);

  /**
   * Manually trigger sync
   */
  const manualSync = useCallback(() => {
    if (isOnline) syncOfflineMembers();
  }, [isOnline, syncOfflineMembers]);

  /**
   * Clear offline members
   */
  const clearOfflineMembers = useCallback(async () => {
    try {
      await db.offlineMembers?.clear();
      setOfflineMembers([]);
    } catch (e) {
      console.error('Failed to clear offline members:', e);
    }
  }, []);

  /**
   * Clear only the items that failed in the last sync attempt
   */
  const clearFailedFromLastSync = useCallback(async () => {
    try {
      if (!Array.isArray(lastFailedIds) || lastFailedIds.length === 0) return;
      for (const id of lastFailedIds) {
        await db.offlineMembers?.delete(id);
      }
      const remaining = await db.offlineMembers?.toArray();
      setOfflineMembers(Array.isArray(remaining) ? remaining : []);
      setLastFailedIds([]);
      setSyncStatus('idle');
      setSyncMessage('');
    } catch (e) {
      console.error('Failed to clear failed offline members:', e);
    }
  }, [lastFailedIds]);

  return {
    offlineMembers,
    syncStatus,
    syncMessage,
    addOfflineMember,
    syncOfflineMembers: manualSync,
    clearOfflineMembers,
    clearFailedFromLastSync,
    hasOfflineMembers: offlineMembers.length > 0
  };
};

export default useOfflineSync;
